import ExcelJS from 'exceljs';
import { minimiseTransactions, calculateExpensePaidMap, calculateExpenseShareMap, calculateExpenseNets } from '@/lib/settlement';
import { formatCurrency } from '@/lib/utils';

/* ── Public interfaces ────────────────────────────────────── */

export interface ExpenseParticipant {
  playerName: string;
  share: number;
}

export interface ShareExpense {
  description: string;
  amount: number;
  payerName: string;
  participants: ExpenseParticipant[];  // per-player expense breakdown
}

export interface ShareSession {
  date: string;
  notes?: string | null;
  players: {
    name: string;
    buyIns: number;
    buyInAmount: number;
    net: number;          // game net (gross gain/loss)
    cashOut: number;
    expensePaid: number;
    expenseShare: number;
  }[];
  expenses: ShareExpense[];
}

export interface PlayerTotal {
  name: string;
  net: number;
  avatar_url?: string | null;
}

/* ── Helpers ─────────────────────────────────────────────── */

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso.split('T')[0];
  }
}

function collectPlayers(sessions: ShareSession[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const s of sessions) {
    for (const p of s.players) {
      if (!seen.has(p.name)) { seen.add(p.name); order.push(p.name); }
    }
  }
  return order;
}

function numFmt(value: number): string {
  const val = formatCurrency(Math.abs(value));
  if (value > 0) return `+${val}`;
  if (value < 0) return `-${val}`;
  return '0.00';
}

/* ── Web Share / download fallback ───────────────────────── */

export async function shareFile(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'SSC Settlement' });
      return;
    } catch {
      // user cancelled or share not supported — fall through to download
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ── ExcelJS colour tokens ───────────────────────────────── */

const C = {
  hdrBg:   'FF1B4332',  // dark-green
  hdrFg:   'FFFFFFFF',
  expBg:   'FF78350F',  // amber-900
  expFg:   'FFFFFFFF',
  buyBg:   'FF1E3A5F',  // deep-blue section header
  buyFg:   'FFFFFFFF',
  netBg:   'FF0F172A',  // slate-900
  netFg:   'FFFFFFFF',
  pos:     'FF16A34A',  // green-600
  neg:     'FFDC2626',  // red-600
  neutral: 'FF64748B',  // slate-500
  rowA:    'FFF8FAFC',  // slate-50
  rowB:    'FFF1F5F9',  // slate-100
  expRowA: 'FFFFF7ED',  // amber-50
  expRowB: 'FFFEF3C7',  // amber-100
  buyRowA: 'FFEFF6FF',  // blue-50
  buyRowB: 'FFDBEAFE',  // blue-100
  border:  'FFE2E8F0',  // slate-200
};

function applyBorder(cell: ExcelJS.Cell) {
  cell.border = {
    top:    { style: 'thin', color: { argb: C.border } },
    bottom: { style: 'thin', color: { argb: C.border } },
    left:   { style: 'thin', color: { argb: C.border } },
    right:  { style: 'thin', color: { argb: C.border } },
  };
}

function styleHeaderRow(row: ExcelJS.Row, bg: string, fg: string, fontSize = 12) {
  row.height = 26;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
    cell.font  = { bold: true, color: { argb: fg }, size: fontSize, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    applyBorder(cell);
  });
  row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
}

function styleValueRow(
  row: ExcelJS.Row,
  bg: string,
  valueColor: (v: number) => string,
  formatVal: (v: number) => string = numFmt,
) {
  row.height = 21;
  row.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
    cell.font  = { size: 11, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: col === 1 ? 'left' : 'center' };
    applyBorder(cell);
    if (col > 1 && cell.value !== null && cell.value !== '' && typeof cell.value === 'number') {
      const v = cell.value as number;
      cell.font = { ...cell.font, bold: true, color: { argb: valueColor(v) } };
      cell.value = v === 0 ? '—' : formatVal(v);
    }
    if (col > 1 && (cell.value === null || cell.value === undefined)) cell.value = '';
  });
}

/* ── Private sheet builders ──────────────────────────────────── */

/**
 * Compact scorecard: ONE row per session, player columns grouped side-by-side.
 * Summary mode  → 1 col/player:  Gross (per session) + Applied Expenses row + Total Net row
 * Detailed mode → 3 cols/player: Buy In | Cash Out | Gross (per session) + Applied Expenses row + Total Net row
 */
function buildScorecardSheet(
  ws: ExcelJS.Worksheet,
  sessions: ShareSession[],
  players: string[],
  sorted: ShareSession[],
  netMap: Record<string, number>,
  detailed: boolean,
): void {
  const CPP = detailed ? 3 : 1;
  const DATE_COL = 1;
  const subLabels = detailed
    ? ['Buy In', 'Cash Out', 'Gross']
    : ['Gross'];

  // Column widths
  ws.getColumn(DATE_COL).width = 18;
  players.forEach((_, pi) => {
    const base = DATE_COL + 1 + pi * CPP;
    if (detailed) {
      ws.getColumn(base).width = 11;
      ws.getColumn(base + 1).width = 11;
      ws.getColumn(base + 2).width = 13;
    } else {
      ws.getColumn(base).width = 13;
    }
  });

  // Title block
  const titleRow = ws.addRow([detailed ? 'SSC Detailed Scorecard' : 'SSC Settlement Scorecard']);
  titleRow.font = { bold: true, size: 14, name: 'Calibri' };
  titleRow.height = 22;
  const dateRange = sorted.length > 1
    ? `${fmtDate(sorted[0].date)} – ${fmtDate(sorted[sorted.length - 1].date)}`
    : sorted.length === 1 ? fmtDate(sorted[0].date) : '';
  ws.addRow([
    `${dateRange}  ·  ${sessions.length} session${sessions.length !== 1 ? 's' : ''}  ·  ${players.length} player${players.length !== 1 ? 's' : ''}`,
  ]).font = { size: 10, name: 'Calibri', color: { argb: C.neutral } };
  ws.addRow([]);

  // Player name header row — merged block of CPP columns per player
  const playerHdrVals: (string | null)[] = [''];
  players.forEach((name) => {
    playerHdrVals.push(name);
    for (let i = 1; i < CPP; i++) playerHdrVals.push(null);
  });
  const playerHdrRow = ws.addRow(playerHdrVals);
  playerHdrRow.height = 28;

  {
    const cell = playerHdrRow.getCell(DATE_COL);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.hdrBg } };
    cell.font = { bold: true, color: { argb: C.hdrFg }, size: 10, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    applyBorder(cell);
  }

  players.forEach((name, pi) => {
    const startCol = DATE_COL + 1 + pi * CPP;
    const endCol = startCol + CPP - 1;
    if (CPP > 1) ws.mergeCells(playerHdrRow.number, startCol, playerHdrRow.number, endCol);
    const cell = playerHdrRow.getCell(startCol);
    cell.value = name;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.hdrBg } };
    cell.font = { bold: true, color: { argb: C.hdrFg }, size: 11, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    applyBorder(cell);
  });

  // Metric sub-header row
  const metricHdrVals: string[] = ['Date'];
  players.forEach(() => subLabels.forEach((l) => metricHdrVals.push(l)));
  const metricHdrRow = ws.addRow(metricHdrVals);
  metricHdrRow.height = 16;
  metricHdrRow.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } };
    cell.font = { size: 8, italic: true, color: { argb: 'FFE2E8F0' }, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: col === DATE_COL ? 'left' : 'center' };
    applyBorder(cell);
  });

  // Compute per-player totals (used in Applied Expenses + TOTAL NET rows)
  const grossTotals: Record<string, number> = {};
  const appliedExpTotals: Record<string, number> = {};
  const buyInTotals: Record<string, number> = {};
  const cashOutTotals: Record<string, number> = {};
  sessions.forEach((s) => {
    s.players.forEach((p) => {
      grossTotals[p.name]      = (grossTotals[p.name]      ?? 0) + p.net;
      appliedExpTotals[p.name] = (appliedExpTotals[p.name] ?? 0) + (p.expensePaid - p.expenseShare);
      buyInTotals[p.name]      = (buyInTotals[p.name]      ?? 0) + p.buyInAmount;
      cashOutTotals[p.name]    = (cashOutTotals[p.name]    ?? 0) + p.cashOut;
    });
  });

  // One data row per session — shows Gross (and Buy In / Cash Out in detailed mode)
  sorted.forEach((s, si) => {
    const bg = si % 2 === 0 ? C.rowA : C.rowB;
    const vals: (string | number)[] = [fmtDate(s.date)];
    players.forEach((name) => {
      const p = s.players.find((pl) => pl.name === name);
      if (p) {
        if (detailed) {
          vals.push(p.buyInAmount, p.cashOut, p.net);
        } else {
          vals.push(p.net);
        }
      } else {
        vals.push(...Array(CPP).fill('—'));
      }
    });

    const row = ws.addRow(vals);
    row.height = 20;
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.font = { size: 10, name: 'Calibri' };
      cell.alignment = { vertical: 'middle', horizontal: col === DATE_COL ? 'left' : 'center' };
      applyBorder(cell);
      if (col > DATE_COL && typeof cell.value === 'number') {
        const metricIdx = (col - DATE_COL - 1) % CPP;
        const v = cell.value as number;
        const isGrossCol = metricIdx === CPP - 1; // last col of group = Gross
        if (detailed && !isGrossCol) {
          // Buy In / Cash Out — plain blue currency
          cell.font = { ...cell.font, color: { argb: 'FF1E40AF' } };
          cell.value = formatCurrency(v);
        } else {
          // Gross — green/red bold
          cell.font = { ...cell.font, bold: true, color: { argb: v > 0 ? C.pos : v < 0 ? C.neg : C.neutral } };
          cell.value = v === 0 ? '—' : numFmt(v);
        }
      }
    });
  });

  // ── Applied Expenses row (total across all sessions per player) ──
  const appliedExpRowVals: (string | number)[] = ['Applied Expenses'];
  players.forEach((name) => {
    if (detailed) {
      // blank Buy In and Cash Out columns; value only in Gross column
      appliedExpRowVals.push('', '', appliedExpTotals[name] ?? 0);
    } else {
      appliedExpRowVals.push(appliedExpTotals[name] ?? 0);
    }
  });
  const appliedExpRow = ws.addRow(appliedExpRowVals);
  appliedExpRow.height = 24;
  appliedExpRow.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.expRowB } };
    cell.font = { bold: true, size: 11, name: 'Calibri', color: { argb: C.expBg } };
    cell.alignment = { vertical: 'middle', horizontal: col === DATE_COL ? 'left' : 'center' };
    applyBorder(cell);
    if (col > DATE_COL && typeof cell.value === 'number') {
      const v = cell.value as number;
      cell.font = { ...cell.font, color: { argb: v > 0 ? C.pos : v < 0 ? C.neg : C.neutral } };
      cell.value = v === 0 ? '—' : numFmt(v);
    }
  });

  // ── TOTAL NET row (Gross total + Applied Expenses = netMap) ──
  const totalVals: (string | number)[] = ['TOTAL NET'];
  players.forEach((name) => {
    if (detailed) {
      // Show buy-in and cash-out totals; Total Net in Gross column
      totalVals.push(buyInTotals[name] ?? 0, cashOutTotals[name] ?? 0, netMap[name] ?? 0);
    } else {
      totalVals.push(netMap[name] ?? 0);
    }
  });
  const totalRow = ws.addRow(totalVals);
  totalRow.height = 30;
  totalRow.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.netBg } };
    cell.font = { bold: true, size: 12, name: 'Calibri', color: { argb: C.netFg } };
    cell.alignment = { vertical: 'middle', horizontal: col === DATE_COL ? 'left' : 'center' };
    applyBorder(cell);
    if (col > DATE_COL && typeof cell.value === 'number') {
      const metricIdx = (col - DATE_COL - 1) % CPP;
      const isGrossCol = metricIdx === CPP - 1;
      const v = cell.value as number;
      if (detailed && !isGrossCol) {
        // Buy In / Cash Out totals in muted blue
        cell.font = { ...cell.font, color: { argb: 'FF93C5FD' } };
        cell.value = v === 0 ? '—' : formatCurrency(v);
      } else {
        // Gross / Total Net — sign-colored bright
        cell.font = { ...cell.font, color: { argb: v > 0 ? 'FF4ADE80' : v < 0 ? 'FFF87171' : 'FF94A3B8' } };
        cell.value = v === 0 ? '—' : numFmt(v);
      }
    }
  });

  // ── Expense Breakdown (embedded below scorecard) ────────────────────
  const hasExpenses = sorted.some(s => s.expenses.length > 0);
  if (hasExpenses) {
    ws.addRow([]);
    ws.addRow([]);

    const expTitleRow = ws.addRow(['Expense Breakdown']);
    expTitleRow.getCell(1).font = { bold: true, size: 13, name: 'Calibri' };
    expTitleRow.height = 22;
    ws.addRow([]);

    const expColHdr = ws.addRow(['Date — Description', 'Payer', 'Total', ...players]);
    styleHeaderRow(expColHdr, C.expBg, C.expFg, 11);

    ws.getColumn(1).width = Math.max((ws.getColumn(1).width as number) || 14, 34);
    ws.getColumn(2).width = Math.max((ws.getColumn(2).width as number) || 0, 14);
    ws.getColumn(3).width = Math.max((ws.getColumn(3).width as number) || 0, 10);
    players.forEach((_, i) => {
      ws.getColumn(4 + i).width = Math.max((ws.getColumn(4 + i).width as number) || 0, 12);
    });

    let expRowIdx = 0;
    sorted.forEach((s) => {
      s.expenses.forEach((exp) => {
        const bg = expRowIdx++ % 2 === 0 ? C.expRowA : C.expRowB;
        const contribMap = calculateExpenseNets([exp], (e, type, part) => {
          if (type === 'payer') return e.payerName;
          return part.playerName;
        });
        const cells: (string | number | null)[] = [
          `${fmtDate(s.date)} — ${exp.description || 'Expense'}`,
          exp.payerName,
          exp.amount,
          ...players.map((n) => contribMap[n] ?? null),
        ];
        const row = ws.addRow(cells);
        row.height = 20;
        row.eachCell({ includeEmpty: true }, (cell, col) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
          cell.font = { size: 10, name: 'Calibri' };
          cell.alignment = { vertical: 'middle', horizontal: col <= 2 ? 'left' : 'center' };
          applyBorder(cell);
          if (col === 3 && typeof cell.value === 'number') {
            cell.value = formatCurrency(cell.value);
            cell.font = { ...cell.font, bold: true };
          }
          if (col > 3 && typeof cell.value === 'number') {
            const v = cell.value as number;
            cell.font = { ...cell.font, bold: true, color: { argb: v > 0 ? C.pos : v < 0 ? C.neg : C.neutral } };
            cell.value = v === 0 ? '—' : numFmt(v);
          }
          if (col > 3 && (cell.value === null || cell.value === undefined)) cell.value = '';
        });
      });
    });
  }

  // ── Session Notes (below expense breakdown) ────────────────────────
  const sessionsWithNotes = sorted.filter(s => s.notes && s.notes.trim());
  if (sessionsWithNotes.length > 0) {
    ws.addRow([]);
    ws.addRow([]);

    const notesTitleRow = ws.addRow(['Session Notes']);
    notesTitleRow.getCell(1).font = { bold: true, size: 13, name: 'Calibri' };
    notesTitleRow.height = 22;
    ws.addRow([]);

    const notesColHdr = ws.addRow(['Date', 'Notes']);
    styleHeaderRow(notesColHdr, C.hdrBg, C.hdrFg, 11);

    const maxCols = Math.max(1 + players.length * CPP, 3 + players.length);
    
    sessionsWithNotes.forEach((s) => {
      const row = ws.addRow([fmtDate(s.date), s.notes?.trim()]);
      const dateCell = row.getCell(1);
      const noteCell = row.getCell(2);
      
      applyBorder(dateCell);
      dateCell.font = { size: 10, name: 'Calibri' };
      dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
      
      applyBorder(noteCell);
      noteCell.font = { size: 10, name: 'Calibri', italic: true };
      noteCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      
      // Merge note cell across remaining columns
      if (maxCols > 2) {
        ws.mergeCells(row.number, 2, row.number, maxCols);
        // Apply borders to all merged cells in the row to maintain consistent look
        for (let c = 3; c <= maxCols; c++) {
          applyBorder(row.getCell(c));
        }
      }
      
      // Approximate height
      const noteLen = s.notes?.trim().length || 0;
      const charsPerLine = 100;
      row.height = Math.max(20, Math.ceil(noteLen / charsPerLine) * 15 + 10);
    });
  }
}

/** One row per expense, columns: Date-Description | Payer | Total | P1 | P2 | ... */
function buildExpenseSheet(
  ws: ExcelJS.Worksheet,
  sessions: ShareSession[],
  players: string[],
  sorted: ShareSession[],
): void {
  ws.getColumn(1).width = 34;
  ws.getColumn(2).width = 14;
  ws.getColumn(3).width = 10;
  players.forEach((_, i) => { ws.getColumn(4 + i).width = 12; });

  ws.addRow(['Expense Breakdown']).font = { bold: true, size: 13, name: 'Calibri' };
  ws.addRow([]);

  const hdrRow = ws.addRow(['Date — Description', 'Payer', 'Total', ...players]);
  styleHeaderRow(hdrRow, C.expBg, C.expFg, 11);

  let rowIdx = 0;
  sorted.forEach((s) => {
    s.expenses.forEach((exp) => {
      const bg = rowIdx++ % 2 === 0 ? C.expRowA : C.expRowB;
      const contribMap = calculateExpenseNets([exp], (e, type, part) => {
        if (type === 'payer') return e.payerName;
        return part.playerName;
      });
      const cells: (string | number | null)[] = [
        `${fmtDate(s.date)} — ${exp.description || 'Expense'}`,
        exp.payerName,
        exp.amount,
        ...players.map((n) => contribMap[n] ?? null),
      ];
      const row = ws.addRow(cells);
      row.height = 20;
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
        cell.font = { size: 10, name: 'Calibri' };
        cell.alignment = { vertical: 'middle', horizontal: col <= 2 ? 'left' : 'center' };
        applyBorder(cell);
        if (col === 3 && typeof cell.value === 'number') {
          cell.value = formatCurrency(cell.value);
          cell.font = { ...cell.font, bold: true };
        }
        if (col > 3 && typeof cell.value === 'number') {
          const v = cell.value as number;
          cell.font = { ...cell.font, bold: true, color: { argb: v > 0 ? C.pos : v < 0 ? C.neg : C.neutral } };
          cell.value = v === 0 ? '—' : numFmt(v);
        }
        if (col > 3 && (cell.value === null || cell.value === undefined)) cell.value = '';
      });
    });
  });
}

/** Who-pays-who settlement instructions. */
function buildSettlementSheet(ws: ExcelJS.Worksheet, netMap: Record<string, number>): void {
  ws.getColumn(1).width = 25;
  ws.getColumn(2).width = 25;
  ws.getColumn(3).width = 15;

  const stHdr = ws.addRow(['From (Debtor)', 'To (Creditor)', 'Amount']);
  styleHeaderRow(stHdr, C.hdrBg, C.hdrFg);

  const playerTotals: PlayerTotal[] = Object.entries(netMap).map(([name, net]) => ({ name, net }));
  const transactions = minimiseTransactions(playerTotals);

  if (transactions.length === 0) {
    ws.addRow(['All settled — no transfers needed']).getCell(1).font = {
      italic: true, color: { argb: C.neutral }, name: 'Calibri',
    };
    return;
  }

  transactions.forEach((t) => {
    const row = ws.addRow([t.from, t.to, t.amount]);
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      applyBorder(cell);
      cell.font = { size: 11, name: 'Calibri' };
      cell.alignment = { vertical: 'middle', horizontal: col === 3 ? 'right' : 'left' };
      if (col === 3) {
        cell.font = { bold: true, size: 11, name: 'Calibri' };
        cell.value = numFmt(t.amount as number);
      }
    });
  });
}

/* ── Summary XLSX — compact pivot (one row per session) ─────── */

export async function buildPivotXLSX(sessions: ShareSession[]): Promise<Blob> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'SSC Score Tracker';

  const players = collectPlayers(sessions);
  const sorted = [...sessions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const netMap: Record<string, number> = {};
  sessions.forEach((s) => {
    s.players.forEach((p) => {
      const playerNet = p.net + p.expensePaid - p.expenseShare;
      netMap[p.name] = Math.round(((netMap[p.name] ?? 0) + playerNet) * 100) / 100;
    });
  });

  const scoreWs = wb.addWorksheet('Scorecard', { views: [{ showGridLines: false }] });
  buildScorecardSheet(scoreWs, sessions, players, sorted, netMap, false);

  const stWs = wb.addWorksheet('Settlement', { views: [{ showGridLines: false }] });
  buildSettlementSheet(stWs, netMap);

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/* ── Full Detailed XLSX — scorecard + entry costs ───────────── */

export async function buildDetailedXLSX(sessions: ShareSession[]): Promise<Blob> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'SSC Score Tracker';

  const players = collectPlayers(sessions);
  const sorted = [...sessions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const netMap: Record<string, number> = {};
  sessions.forEach((s) => {
    s.players.forEach((p) => {
      const playerNet = p.net + p.expensePaid - p.expenseShare;
      netMap[p.name] = Math.round(((netMap[p.name] ?? 0) + playerNet) * 100) / 100;
    });
  });

  const scoreWs = wb.addWorksheet('Scorecard', { views: [{ showGridLines: false }] });
  buildScorecardSheet(scoreWs, sessions, players, sorted, netMap, true);

  const stWs = wb.addWorksheet('Settlement', { views: [{ showGridLines: false }] });
  buildSettlementSheet(stWs, netMap);

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/* ── Summary Image (canvas pivot table render) ───────────── */

export function buildSummaryImageBlob(
  sessions: ShareSession[],
  _playerTotals: PlayerTotal[],
  dateLabel: string,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const players = collectPlayers(sessions);

    const COL_W = 110;
    const LABEL_W = 150;
    const ROW_H = 30;
    const PAD = 20;
    const W = Math.max(LABEL_W + players.length * COL_W + PAD * 2, 500);

    const sorted = [...sessions].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const expRows = sorted.reduce((s, sess) => s + sess.expenses.length, 0);
    const hasExp = expRows > 0;

    // Height: title + game header + session rows + blank + exp header + exp rows + blank + net + footer
    const rowCount = 1 + sorted.length + (hasExp ? 1 + expRows + 1 : 1) + 1 + 1;
    const H = PAD + 50 + rowCount * ROW_H + PAD;

    const canvas = document.createElement('canvas');
    canvas.width = W * dpr;
    canvas.height = H * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) { reject(new Error('Canvas not available')); return; }
    ctx.scale(dpr, dpr);

    const font = (size: number, bold = false) =>
      `${bold ? 700 : 400} ${size}px Inter, system-ui, sans-serif`;

    /* Background */
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, W, H);

    /* Title bar */
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, '#166534');
    grad.addColorStop(1, '#15803d');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.roundRect(PAD, PAD, W - PAD * 2, 42, 10); ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = font(15, true);
    ctx.fillText('🃏  SSC Settlement Summary', PAD + 14, PAD + 27);
    ctx.fillStyle = 'rgba(255,255,255,.55)';
    ctx.font = font(11);
    const dl = ctx.measureText(dateLabel).width;
    ctx.fillText(dateLabel, W - PAD - dl - 10, PAD + 27);

    let y = PAD + 56;
    const tableW = LABEL_W + players.length * COL_W;

    const drawCell = (
      x: number, cy: number, w: number, text: string,
      { bg = '#1e293b', fg = '#e2e8f0', bold = false, fs = 11 } = {}
    ) => {
      ctx.fillStyle = bg;
      ctx.fillRect(x, cy, w, ROW_H);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x + 0.25, cy + 0.25, w - 0.5, ROW_H - 0.5);
      ctx.fillStyle = fg;
      ctx.font = font(fs, bold);
      const tw = ctx.measureText(text).width;
      ctx.fillText(text, x + (w - tw) / 2, cy + ROW_H * 0.66);
    };

    const drawLabelCell = (cy: number, text: string, bg: string, fg: string, fs = 11) => {
      drawCell(PAD, cy, LABEL_W, '', { bg });
      ctx.fillStyle = fg;
      ctx.font = font(fs, true);
      const display = text.length > 22 ? text.slice(0, 20) + '…' : text;
      ctx.fillText(display, PAD + 8, cy + ROW_H * 0.66);
    };

    /* Section header */
    const sectionHdr = (cy: number, label: string, bg: string, fg: string) => {
      drawLabelCell(cy, label, bg, fg, 11);
      players.forEach((_, i) => drawCell(PAD + LABEL_W + i * COL_W, cy, COL_W, players[i].length > 10 ? players[i].slice(0, 9) + '…' : players[i], { bg, fg, bold: true, fs: 11 }));
    };

    /* GAME RESULTS */
    sectionHdr(y, 'Game Results (Gross)', '#1b4332', '#fff');
    y += ROW_H;

    sorted.forEach((s, si) => {
      const bg = si % 2 === 0 ? '#1e293b' : '#273344';
      const playerMap: Record<string, number> = {};
      s.players.forEach((p) => { playerMap[p.name] = p.net; });
      drawLabelCell(y, fmtDate(s.date), bg, '#cbd5e1', 10);
      players.forEach((name, i) => {
        const v = playerMap[name];
        const txt = v === undefined ? '—' : (v === 0 ? '0.00' : numFmt(v));
        const fg = v === undefined || v === 0 ? '#64748b' : v > 0 ? '#4ade80' : '#f87171';
        drawCell(PAD + LABEL_W + i * COL_W, y, COL_W, txt, { bg, fg, bold: v !== undefined && v !== 0, fs: 11 });
      });
      y += ROW_H;
    });

    y += 6;

    /* EXPENSES */
    if (hasExp) {
      sectionHdr(y, 'Expenses', '#78350f', '#fef9c3');
      y += ROW_H;

      sorted.forEach((s) => {
        s.expenses.forEach((exp, ei) => {
          const bg = ei % 2 === 0 ? '#292524' : '#1c1917';
          const contribMap = calculateExpenseNets([exp], (e, type, part) => {
            if (type === 'payer') return e.payerName;
            return part.playerName;
          });
          const label = `${fmtDate(s.date)} — ${exp.description || 'Expense'}`;
          drawLabelCell(y, label, bg, '#d6d3d1', 9);
          players.forEach((name, i) => {
            const v = contribMap[name];
            const txt = v === undefined ? '' : numFmt(v);
            const fg = v === undefined ? '#44403c' : v > 0 ? '#86efac' : '#fca5a5';
            drawCell(PAD + LABEL_W + i * COL_W, y, COL_W, txt, { bg, fg, bold: v !== undefined, fs: 10 });
          });
          y += ROW_H;
        });
      });

      y += 6;
    }

    /* NET TOTAL */
    const netMap: Record<string, number> = {};
    sessions.forEach((s) => {
      s.players.forEach((p) => {
        if (!netMap[p.name]) netMap[p.name] = 0;
        const playerNet = p.net + p.expensePaid - p.expenseShare;
        netMap[p.name] = Math.round((netMap[p.name] + playerNet) * 100) / 100;
      });
    });

    drawLabelCell(y, 'NET TOTAL', '#0f172a', '#fff', 12);
    ctx.strokeStyle = '#4ade80'; ctx.lineWidth = 1.5;
    ctx.strokeRect(PAD + 0.75, y + 0.75, LABEL_W - 1.5, ROW_H - 1.5);
    players.forEach((name, i) => {
      const v = netMap[name] || 0;
      const txt = v === 0 ? '—' : numFmt(v);
      const fg = v > 0 ? '#4ade80' : v < 0 ? '#f87171' : '#94a3b8';
      drawCell(PAD + LABEL_W + i * COL_W, y, COL_W, txt, { bg: '#0f172a', fg, bold: true, fs: 12 });
      ctx.strokeStyle = '#4ade80'; ctx.lineWidth = 1.5;
      ctx.strokeRect(PAD + LABEL_W + i * COL_W + 0.75, y + 0.75, COL_W - 1.5, ROW_H - 1.5);
    });
    y += ROW_H + 10;

    /* Footer */
    ctx.fillStyle = '#334155';
    ctx.font = font(10);
    ctx.fillText('Generated by SSC Score Tracker', PAD + 8, y + 12);

    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob failed'));
    }, 'image/png');
  });
}
